/**
 * Space Management - Types for the unified space overview page
 *
 * Provides grouped views of units by building (area) with
 * allocation counts, contract aircraft counts, and configurable columns.
 */

import type { AreaType, HangarStatus } from './hangars'
import type { UnitType, UnitStatus, CapacityStrategy, DoorType, RateType } from './units'

// ============================================
// SPACE MANAGEMENT UNIT (flat row for table)
// ============================================

export interface SpaceManagementUnit {
  // Identity
  id: string
  unit_code: string
  name: string | null
  area_id: string
  location_id: string

  // Classification
  type: UnitType | null
  capacity_strategy: CapacityStrategy | null

  // Dimensions
  usable_sqft: number | null
  interior_width: number | null
  interior_depth: number | null

  // Door & Constraints
  door_width: number | null
  door_height: number | null
  door_type: DoorType | null
  max_wingspan: number | null
  max_length: number | null
  max_tail_height: number | null

  // Status
  status: UnitStatus
  is_leasable: boolean | null
  current_lease_id: string | null

  // Rates (unit-level overrides or defaults)
  default_monthly_rate: number | null
  default_transient_rate: number | null
  rate_type: RateType | null
  pricing_override: boolean | null
  unit_monthly_rate: number | null
  unit_rate_per_sqft: number | null

  // Computed counts (joined client-side)
  placed_aircraft_count: number
  assigned_aircraft_count: number
  occupied_sqft: number

  // Lease info (for tenant display)
  lease_monthly_rate: number | null
  lease_client_name: string | null
  lease_status: string | null

  // Parent area info (for grouping)
  area_name: string
  area_code: string
  area_type: AreaType
}

// ============================================
// SPACE MANAGEMENT AREA (group header)
// ============================================

export interface SpaceManagementArea {
  id: string
  name: string
  area_code: string
  type: AreaType
  status: HangarStatus
  total_footprint_sqft: number
  exterior_width: number
  exterior_depth: number

  // Area-level rates
  monthly_rate_private: number | null
  monthly_rate_shared: number | null
  transient_rate_nightly: number | null

  // Child units
  units: SpaceManagementUnit[]

  // Aggregates (computed client-side from units)
  total_units: number
  total_usable_sqft: number
  total_placed_aircraft: number
  total_assigned_aircraft: number
  units_available: number
  units_leased: number
  units_maintenance: number
}

// ============================================
// CATEGORY TAB DEFINITIONS
// ============================================

export type SpaceCategoryTab = 'all' | 'hangars' | 'tiedowns' | 'ramp' | 'other'

export interface SpaceCategoryTabDef {
  value: SpaceCategoryTab
  label: string
  /** Area types to include. null = all */
  areaTypes: AreaType[] | null
}

export const SPACE_CATEGORY_TABS: SpaceCategoryTabDef[] = [
  { value: 'all', label: 'All', areaTypes: null },
  {
    value: 'hangars',
    label: 'Hangars',
    areaTypes: [
      'single_unit_hangar',
      'box_hangar_single_row',
      'box_hangar_double_row',
      't_hangar_row',
      'nested_t_hangar_row',
    ],
  },
  { value: 'tiedowns', label: 'Tiedowns', areaTypes: ['tiedown_area'] },
  { value: 'ramp', label: 'Ramp & Shade', areaTypes: ['ramp_area', 'shade_structure'] },
  { value: 'other', label: 'Other', areaTypes: ['terminal', 'office', 'custom'] },
]

// ============================================
// COLUMN GROUP DEFINITIONS
// ============================================

export type ColumnGroupKey = 'basic' | 'status' | 'constraints' | 'performance' | 'rates'

export interface ColumnGroupDef {
  key: ColumnGroupKey
  label: string
  description: string
  defaultVisible: boolean
  /** Column IDs in the TanStack Table */
  columns: string[]
}

export const COLUMN_GROUPS: ColumnGroupDef[] = [
  {
    key: 'basic',
    label: 'Basic',
    description: 'Unit code, name, category, size',
    defaultVisible: true,
    columns: ['unit_code', 'name', 'type', 'usable_sqft'],
  },
  {
    key: 'status',
    label: 'Status',
    description: 'Status, placed aircraft, assigned aircraft, tenant',
    defaultVisible: true,
    columns: ['status', 'placed_aircraft_count', 'assigned_aircraft_count', 'lease_client_name'],
  },
  {
    key: 'constraints',
    label: 'Constraints',
    description: 'Door dimensions, max wingspan',
    defaultVisible: false,
    columns: ['door_height', 'door_width', 'max_wingspan'],
  },
  {
    key: 'performance',
    label: 'Performance',
    description: 'Expected monthly rate, occupancy placeholder',
    defaultVisible: false,
    columns: ['expected_monthly_rate', 'occupancy_placeholder'],
  },
  {
    key: 'rates',
    label: 'Rates',
    description: 'Monthly rate, transient rate',
    defaultVisible: false,
    columns: ['default_monthly_rate', 'default_transient_rate'],
  },
]
